/**
 * A REFERÊNCIA DO KLEIN, REDUZIDA — T21 (12/10/2026).
 *
 * O FLUX.2 [klein] aceita até 4 imagens de referência de no máximo 512 px, e a bancada do 0049
 * mediu com 512. Quem reduz é a tela, porque a imagem já está aqui como data URI: mandar 1024 px
 * ao servidor para ele reduzir seria o dobro da rede de uma escola para nada.
 */

/** A medida reduzida, mantendo a proporção. Pura, para o teste não precisar de canvas. */
export function medidaReduzida(largura: number, altura: number, maximo = 512): { largura: number; altura: number } {
  if (largura <= 0 || altura <= 0) return { largura: maximo, altura: maximo };
  const fator = Math.min(1, maximo / Math.max(largura, altura));
  return { largura: Math.max(1, Math.round(largura * fator)), altura: Math.max(1, Math.round(altura * fator)) };
}

/**
 * Reduz uma imagem (data URI ou URL do mesmo site) a no máximo 512 px e devolve JPEG.
 *
 * JPEG e não PNG: a referência viaja no corpo do pedido, e um PNG de 512 px de desenho colorido
 * passa de 400 KB; o JPEG a 0,9 fica perto de 60 KB e o klein não perde o personagem por isso.
 * Fundo transparente vira BRANCO — é o fundo que o personagem isolado tem na galeria.
 */
/**
 * T25 · A CAIXA DO PERSONAGEM: onde há pixel que não é fundo (nem transparente, nem quase branco),
 * com 6% de margem. Medido em 13/10 (rastro/73-proporcao-e-cor): o personagem salvo chegava ao
 * desenhista pequeno no meio do branco — no print do operador, 147×245 px de personagem —, e a pose
 * nova trocava a roupa em 2 de 4. Recortado e a 1024 px, 4 de 4 mantiveram a roupa.
 * `null` quando não há personagem nenhum (imagem toda branca): aí se usa a imagem inteira.
 */
export function caixaDoPersonagem(
  rgba: ArrayLike<number>,
  largura: number,
  altura: number
): { x: number; y: number; largura: number; altura: number } | null {
  let x0 = largura, y0 = altura, x1 = -1, y1 = -1;
  for (let y = 0; y < altura; y++) {
    for (let x = 0; x < largura; x++) {
      const i = (y * largura + x) * 4;
      const fundo = rgba[i + 3] < 128 || (rgba[i] > 245 && rgba[i + 1] > 245 && rgba[i + 2] > 245);
      if (fundo) continue;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
    }
  }
  if (x1 < 0) return null;
  const m = Math.round(0.06 * Math.max(x1 - x0 + 1, y1 - y0 + 1));
  const x = Math.max(0, x0 - m), y = Math.max(0, y0 - m);
  return { x, y, largura: Math.min(largura, x1 + 1 + m) - x, altura: Math.min(altura, y1 + 1 + m) - y };
}

export function reduzirParaReferencia(src: string, maximo = 512, recortarNoPersonagem = false): Promise<string> {
  return new Promise((resolver, recusar) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      let origem = { x: 0, y: 0, largura: img.naturalWidth, altura: img.naturalHeight };
      if (recortarNoPersonagem) {
        const leitura = document.createElement("canvas");
        leitura.width = img.naturalWidth;
        leitura.height = img.naturalHeight;
        const lctx = leitura.getContext("2d");
        if (lctx) {
          lctx.drawImage(img, 0, 0);
          try {
            const dados = lctx.getImageData(0, 0, leitura.width, leitura.height).data;
            origem = caixaDoPersonagem(dados, leitura.width, leitura.height) ?? origem;
          } catch {
            /* imagem de outro site sem CORS: segue inteira */
          }
        }
      }
      const m = medidaReduzida(origem.largura, origem.altura, maximo);
      const canvas = document.createElement("canvas");
      canvas.width = m.largura;
      canvas.height = m.altura;
      const ctx = canvas.getContext("2d");
      if (!ctx) return recusar(new Error("sem canvas"));
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, m.largura, m.altura);
      ctx.drawImage(img, origem.x, origem.y, origem.largura, origem.altura, 0, 0, m.largura, m.altura);
      resolver(canvas.toDataURL("image/jpeg", 0.9));
    };
    img.onerror = () => recusar(new Error("não deu para abrir a imagem de referência"));
    img.src = src;
  });
}
